import { Fragment, useState, type CSSProperties, type Ref } from 'react'
import { Bold, CaseSensitive, Check, Code, EyeOff, Heading1, Heading2, Heading3, Italic, Link, Strikethrough, TextQuote, Underline, type LucideIcon } from 'lucide-react'
import { isFormatActive, linkAt, linkBlocker, type Format } from '@/lib/textFormat'
import { cx } from '@/lib/cx'
import { isMac, type Selection, type ToolbarState } from './useFormatToolbar'
import styles from './FormatToolbar.module.css'

interface Item {
  format: Format
  label: string
  icon: LucideIcon
  /** Буква сочетания с Ctrl (⌘ на маке). */
  key?: string
}

const GROUPS: Item[][] = [
  [
    { format: 'h1', label: 'Заголовок 1', icon: Heading1 },
    { format: 'h2', label: 'Заголовок 2', icon: Heading2 },
    { format: 'h3', label: 'Заголовок 3', icon: Heading3 },
    { format: 'text', label: 'Обычный текст', icon: CaseSensitive },
  ],
  [
    { format: 'bold', label: 'Жирный', icon: Bold, key: 'B' },
    { format: 'italic', label: 'Курсив', icon: Italic, key: 'I' },
    { format: 'underline', label: 'Подчёркнутый', icon: Underline, key: 'U' },
    { format: 'strike', label: 'Зачёркнутый', icon: Strikethrough },
  ],
  [
    { format: 'quote', label: 'Цитата', icon: TextQuote },
    { format: 'code', label: 'Код', icon: Code },
    { format: 'spoiler', label: 'Спойлер', icon: EyeOff },
  ],
]

/** Почему ссылку из выделения не сделать — в подсказке к кнопке. */
const LINK_BLOCKED = {
  lines: 'Ссылка: выделите текст в одной строке',
  brackets: 'Ссылка: выделите текст без квадратных скобок',
  empty: 'Ссылка: выделите текст',
}

/**
 * Плашка над выделенным текстом. Фокус не забирает: нажатие мышью гасится, и выделение
 * в поле остаётся. С клавиатуры — Ctrl/⌘+B, I, U прямо в поле, поэтому кнопки не в порядке Tab.
 * Исключение — поле для адреса ссылки: в него фокус уходит, иначе адрес не набрать.
 * Положение выставляет useFormatToolbar.
 */
export function FormatToolbar({ ref, text, range, linking, onFormat, onLink, onLinkSubmit, onLinkCancel }: { ref: Ref<HTMLDivElement>; text: string } & ToolbarState) {
  return (
    <div
      ref={ref}
      className={cx(styles.toolbar, 'klichat-popover')}
      style={{ '--popover-origin': 'bottom left' } as CSSProperties}
      role="toolbar"
      aria-label="Форматирование"
      onMouseDown={(e) => {
        if (!(e.target instanceof HTMLInputElement)) e.preventDefault()
      }}
    >
      {linking ? <LinkField onSubmit={onLinkSubmit} onCancel={onLinkCancel} /> : <Buttons text={text} range={range} onFormat={onFormat} onLink={onLink} />}
    </div>
  )
}

function Buttons({ text, range, onFormat, onLink }: { text: string; range: Selection; onFormat: (format: Format) => void; onLink: () => void }) {
  const link = linkAt(text, range.start, range.end)
  const blocked = link ? null : linkBlocker(text, range.start, range.end)
  return (
    <>
      {GROUPS.map((group, i) => (
        <Fragment key={i}>
          {i > 0 ? <span className={styles.divider} aria-hidden="true" /> : null}
          {group.map(({ format, label, icon: Icon, key }) => (
            <button
              key={format}
              type="button"
              tabIndex={-1}
              className={styles.button}
              data-tooltip={key ? `${label} (${isMac ? '⌘' : 'Ctrl+'}${key})` : label}
              aria-label={label}
              aria-keyshortcuts={key ? `${isMac ? 'Meta' : 'Control'}+${key}` : undefined}
              aria-pressed={format === 'text' ? undefined : isFormatActive(text, range.start, range.end, format)}
              onClick={() => onFormat(format)}
            >
              <Icon size={18} />
            </button>
          ))}
        </Fragment>
      ))}
      <span className={styles.divider} aria-hidden="true" />
      <button
        type="button"
        tabIndex={-1}
        className={styles.button}
        data-tooltip={blocked ? LINK_BLOCKED[blocked] : 'Ссылка'}
        aria-label="Ссылка"
        aria-pressed={Boolean(link)}
        disabled={blocked !== null}
        onClick={onLink}
      >
        <Link size={18} />
      </button>
    </>
  )
}

/**
 * Адрес ссылки вместо кнопок. Enter — поставить, Esc — назад к кнопкам. Без схемы адрес
 * получает https; не адрес сайта — поле краснеет и ждёт.
 */
function LinkField({ onSubmit, onCancel }: { onSubmit: (raw: string) => boolean; onCancel: (back: boolean) => void }) {
  const [value, setValue] = useState('')
  const [invalid, setInvalid] = useState(false)
  const submit = () => {
    if (value.trim() && !onSubmit(value)) setInvalid(true)
  }
  return (
    <>
      <Link size={18} className={styles.linkIcon} aria-hidden="true" />
      <input
        // поле открывают кнопкой «Ссылка», чтобы сразу вставить адрес
        autoFocus
        className={styles.linkInput}
        inputMode="url"
        spellCheck={false}
        autoComplete="off"
        placeholder="Вставьте ссылку"
        aria-label="Адрес ссылки"
        aria-invalid={invalid || undefined}
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          setInvalid(false)
        }}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing) return
          if (e.key === 'Enter') {
            e.preventDefault()
            submit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            onCancel(true)
          }
        }}
        onBlur={(e) => {
          if (!e.currentTarget.parentElement?.contains(e.relatedTarget)) onCancel(false)
        }}
      />
      <button type="button" tabIndex={-1} className={styles.button} data-tooltip="Добавить ссылку (Enter)" aria-label="Добавить ссылку" disabled={!value.trim()} onClick={submit}>
        <Check size={18} />
      </button>
    </>
  )
}
